import React, { useEffect, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { FiCheck, FiGlobe } from 'react-icons/fi';
import { type Language, useI18n } from '../i18n';
import {
  LANGUAGE_METADATA_KEY,
  languagePreferenceService,
} from '../services/languagePreferenceService';
import './LanguageOnboarding.css';

interface LanguageOnboardingProps {
  session: Session;
  children: React.ReactNode;
}

const choices: Array<{
  value: Language;
  nativeName: string;
  description: string;
}> = [
  {
    value: 'en',
    nativeName: 'English',
    description: 'Use the portal in English',
  },
  {
    value: 'fr',
    nativeName: 'Français',
    description: 'Utiliser le portail en français',
  },
];

export default function LanguageOnboarding({ session, children }: LanguageOnboardingProps) {
  const { language, setLanguage } = useI18n();
  const [selectedLanguage, setSelectedLanguage] = useState<Language>(language);
  const [isChecking, setIsChecking] = useState(true);
  const [needsChoice, setNeedsChoice] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const preference = languagePreferenceService.getPreference(session.user);
    if (preference) {
      setLanguage(preference);
      setSelectedLanguage(preference);
      setNeedsChoice(false);
      if (!session.user.user_metadata?.[LANGUAGE_METADATA_KEY]) {
        void languagePreferenceService.savePreference(session.user.id, preference);
      }
    } else {
      setNeedsChoice(true);
    }
    setIsChecking(false);
  }, [session.user, setLanguage]);

  const confirmLanguage = async () => {
    setIsSaving(true);
    setLanguage(selectedLanguage);
    await languagePreferenceService.savePreference(session.user.id, selectedLanguage);
    setNeedsChoice(false);
    setIsSaving(false);
  };

  if (isChecking) {
    return <div className="language-onboarding__loading"><div className="spinner" /></div>;
  }

  if (!needsChoice) return <>{children}</>;

  return (
    <div className="language-onboarding" role="dialog" aria-modal="true" aria-labelledby="language-onboarding-title">
      <div className="language-onboarding__glow language-onboarding__glow--one" />
      <div className="language-onboarding__glow language-onboarding__glow--two" />
      <section className="language-onboarding__card">
        <div className="language-onboarding__icon"><FiGlobe aria-hidden="true" /></div>
        <p className="language-onboarding__eyebrow">Welcome · Bienvenue</p>
        <h1 id="language-onboarding-title">Choose your language</h1>
        <p className="language-onboarding__subtitle">Choisissez votre langue pour continuer</p>

        <div className="language-onboarding__choices">
          {choices.map((choice) => {
            const isSelected = selectedLanguage === choice.value;
            return (
              <button
                key={choice.value}
                type="button"
                className={`language-onboarding__choice ${isSelected ? 'language-onboarding__choice--selected' : ''}`}
                onClick={() => setSelectedLanguage(choice.value)}
                aria-pressed={isSelected}
              >
                <span className="language-onboarding__code">{choice.value.toUpperCase()}</span>
                <span className="language-onboarding__choice-copy">
                  <strong>{choice.nativeName}</strong>
                  <small>{choice.description}</small>
                </span>
                <span className="language-onboarding__check" aria-hidden="true">
                  {isSelected && <FiCheck />}
                </span>
              </button>
            );
          })}
        </div>

        <button
          type="button"
          className="language-onboarding__continue"
          onClick={() => { void confirmLanguage(); }}
          disabled={isSaving}
        >
          {isSaving ? 'Saving…' : selectedLanguage === 'fr' ? 'Continuer' : 'Continue'}
        </button>
        <p className="language-onboarding__note">
          You can change this later in Settings · Modifiable plus tard dans les paramètres
        </p>
      </section>
    </div>
  );
}
